import { Box, Rating, Stack, Typography } from "@mui/material";
import React from "react";
import Image from "next/image";
import { Link } from "@/i18n/routing";
import { Image as ReviewImage } from "@/types/dto/common/image";

interface ReviewTestimonial {
  image?: ReviewImage;
  starRating?: number;
  title?: string;
  copy?: string;
  reviewerNameSocialMediaHandle?: string;
  cta?: {
    cta: {
      url: string;
    };
  };
}

interface ReviewMediaCardProps {
  testimonial: ReviewTestimonial;
  index: number;
}

const ReviewMediaCard = ({ testimonial, index }: ReviewMediaCardProps) => {
  const {
    image = {},
    starRating = 0,
    title = "",
    copy = "",
    reviewerNameSocialMediaHandle = "",
    cta
  } = testimonial;

  return (
    <Stack
      sx={{
        flexDirection: { xs: "column", lg: "row-reverse" },
        gap: { xs: "2.4rem", lg: "4rem" }
      }}
      data-testid={`review-media-card-${index}`}
      data-carousel-position={String(index + 1)}
    >
      <Box
        sx={{
          width: { xs: "100%", lg: "52%", xl: "60.1rem" },
          maxWidth: { lg: "62rem" },
          height: { xs: "22.5rem", sm: "30rem", lg: "40.8rem" },
          position: "relative"
        }}
      >
        {image?.imageUrl ? (
          <Image
            data-testid={`review-image-${index}`}
            src={image.imageUrl}
            alt={image?.altText || ""}
            fill
            style={{
              objectFit: "cover",
              borderRadius: "2rem",
              userSelect: "none"
            }}
            sizes="(max-width: 991px) 100vw, 1720px"
          />
        ) : null}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: { xs: "100%", md: "100%", lg: "48%", xl: "56rem" },
          maxWidth: { lg: "56rem" },
          gap: { xs: "1rem" }
        }}
      >
        <Image
          src="/images/quote-black.svg"
          alt={title || "Quote"}
          width={46}
          height={32}
          data-testid={`review-quote-icon-${index}`}
        />
        <Rating
          value={starRating}
          data-testid={`review-star-rating-${index}`}
          disabled
          precision={0.1}
          readOnly
          size="small"
          sx={{
            "&.Mui-disabled": {
              color: "accent.gold",
              opacity: 1
            }
          }}
        />
        <Stack gap="1.6rem">
          <Typography
            variant="subtitle_lg"
            fontWeight={400}
            fontSize="1.8rem"
            sx={{ color: "primary.dark" }}
            textAlign="center"
            lineHeight="150%"
            data-testid={`review-copy-${index}`}
          >
            {copy}
          </Typography>
          <Stack gap="1rem" alignItems="center">
          <Typography
            variant="subtitle_lg"
            sx={{ color: "primary.dark" }}
            fontWeight={600}
            fontSize="1.8rem"
            textAlign="center"
            lineHeight="150%"
            data-testid={`review-reviewer-${index}`}
            >
            - {reviewerNameSocialMediaHandle}
          </Typography>
            {cta?.cta?.url && (
              <Link
                href={cta.cta.url}
                style={{
                  textDecoration: "none"
                }}
              >
                <Typography
                  variant="subtitle_lg"
                  fontWeight={600}
                  sx={{
                    color: "secondary.main",
                    margin: "0 !important",
                    "&:hover": {
                      textDecoration: "underline"
                    }
                  }}
                  fontSize="1.8rem"
                  lineHeight="150%"
                  textAlign="center"
                  data-testid={`review-title-${index}`}
                >
                  {title}
                </Typography>
              </Link>
            )}
            </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};

export default ReviewMediaCard;
